'use client';

import { LoaderCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import styles from './index.module.less';

interface StepItem {
  text: string;
  duration: number;
}

interface LoadingProgressProps {
  onComplete?: () => void;
  currentStep: number;
  setCurrentStep: (step: number) => void;
  searchingText: string;
  steps: StepItem[];
}

export default function LoadingProgress({
  onComplete,
  currentStep,
  setCurrentStep,
  searchingText,
  steps,
}: LoadingProgressProps) {
  const [progress, setProgress] = useState(0);
  const onCompleteRef = useRef(onComplete);

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    if (currentStep >= steps.length) {
      setProgress(100);
      onCompleteRef.current?.();
      return;
    }

    const isLastStep = currentStep === steps.length - 1;
    const stepDuration = steps[currentStep].duration;
    const interval = 50; // 更新间隔
    const totalUpdates = stepDuration / interval;
    let updates = 0;

    setProgress((currentStep / steps.length) * 100);

    const timer = setInterval(() => {
      updates++;
      const stepProgress = (updates / totalUpdates) * 100;
      const totalProgress = ((currentStep + stepProgress / 100) / steps.length) * 100;

      // 如果是最后一步，最多只到90%
      if (isLastStep) {
        setProgress(Math.min(totalProgress, 90));
      } else {
        setProgress(Math.min(totalProgress, 100));
      }

      // 最后一步不自动前进，需要手动调用 setCurrentStep(steps.length) 来完成
      if (updates >= totalUpdates && !isLastStep) {
        clearInterval(timer);
        setCurrentStep(currentStep + 1);
      }
    }, interval);

    return () => clearInterval(timer);
  }, [currentStep, setCurrentStep, steps.length]);

  const getStepStatus = (index: number) => {
    if (index < currentStep) return 'completed';
    if (index === currentStep) return 'active';
    return 'pending';
  };

  const getStepTextClass = (index: number) => {
    const status = getStepStatus(index);
    if (status === 'completed') return styles.stepTextCompleted;
    if (status === 'active') return styles.stepTextActive;
    return styles.stepTextPending;
  };

  return (
    <div className={styles.container}>
      {/* 进度百分比 */}
      <div className={styles.percentageSection}>
        <div className={styles.percentageText}>{Math.round(progress)}%</div>
      </div>

      {/* Searching文案 */}
      <div className={styles.searchingText}>{searchingText}</div>

      {/* 进度条 */}
      <div className={styles.progressBarContainer}>
        <div className={styles.progressBar} style={{ width: `${progress}%` }} />
      </div>

      {/* 步骤列表 */}
      <div className={styles.stepsList}>
        {steps.map((step, index) => {
          const status = getStepStatus(index);
          return (
            <div key={index} className={styles.stepItem}>
              {/* 状态图标 */}
              <div className={styles.iconContainer}>
                {status === 'completed' ? (
                  // 完成状态 - 绿色勾选
                  <div className={styles.checkIcon}>
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path
                        d="M3 10L8 15L17 6"
                        stroke="#2ECE5E"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </div>
                ) : status === 'active' ? (
                  // 进行中状态 - 旋转圆圈
                  <LoaderCircle size={16} className={styles.spinnerIcon} />
                ) : (
                  // 待处理状态 - 空心圆圈
                  <div className={styles.pendingIcon} />
                )}
              </div>

              {/* 步骤文本 */}
              <span className={`${styles.stepText} ${getStepTextClass(index)}`}>{step.text}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
